import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaExpired, setCaptchaExpired] = useState(false);
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaToken) {
      setError('캡차 인증을 완료해주세요.');
      return;
    }

    if (captchaExpired) {
      setError('캡차가 만료되었습니다. 다시 인증해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');

    const { error } = await signIn(email, password, captchaToken);
    
    if (error) {
      setError(error.message);
    } else {
      toast({
        title: "로그인 성공",
        description: "환영합니다!",
      });
      navigate('/');
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaToken) {
      setError('캡차 인증을 완료해주세요.');
      return;
    }

    if (captchaExpired) {
      setError('캡차가 만료되었습니다. 다시 인증해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');

    const { error } = await signUp(email, password, captchaToken);
    
    if (error) {
      setError(error.message);
    } else {
      toast({
        title: "회원가입 성공",
        description: "이메일을 확인해주세요.",
      });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <SEOHead 
        title="로그인 - Central TG"
        description="Central TG 계정에 로그인하여 서비스를 이용하세요."
        noindex={true}
      />
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Central TG Inc.</CardTitle>
          <CardDescription className="text-center">
            계정에 로그인하거나 새 계정을 만드세요
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">로그인</TabsTrigger>
              <TabsTrigger value="signup">회원가입</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">이메일</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="이메일을 입력하세요"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">비밀번호</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="비밀번호를 입력하세요"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>보안 인증</Label>
                  <HCaptcha
                    sitekey="7420569c-a2a6-467c-a470-b95c2b9e23a4"
                    onVerify={(token) => {
                      setCaptchaToken(token);
                      setCaptchaExpired(false);
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                      setCaptchaExpired(true);
                    }}
                  />
                  {captchaExpired && (
                    <p className="text-sm text-destructive">
                      캡차가 만료되었습니다. 다시 인증해주세요.
                    </p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !captchaToken || captchaExpired}
                >
                  {loading ? "로그인 중..." : !captchaToken ? "캡차 인증 후 로그인" : "로그인"}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">이메일</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="이메일을 입력하세요"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">비밀번호</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="비밀번호를 입력하세요 (최소 6자)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label>보안 인증</Label>
                  <HCaptcha
                    sitekey="7420569c-a2a6-467c-a470-b95c2b9e23a4"
                    onVerify={(token) => {
                      setCaptchaToken(token);
                      setCaptchaExpired(false);
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                      setCaptchaExpired(true);
                    }}
                  />
                  {captchaExpired && (
                    <p className="text-sm text-destructive">
                      캡차가 만료되었습니다. 다시 인증해주세요.
                    </p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !captchaToken || captchaExpired}
                >
                  {loading ? "가입 중..." : !captchaToken ? "캡차 인증 후 회원가입" : "회원가입"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
